import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Scenario } from 'src/app/core/models/scenario';
import * as fromUIStore from 'src/app/core/store';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { DialogMode } from 'src/app/shared/atlas-dialog/enums/dialog-mode.enum';
import { formatUserInitials } from 'src/app/shared/utils/name.utils';
import { itemNameValidator } from 'src/app/shared/utils/form-validators/itemName.validator';

@Component({
  selector: 'atlas-add-edit-scenario-form',
  templateUrl: './add-edit-scenario-form.component.html',
  styleUrls: ['./add-edit-scenario-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AddEditScenarioFormComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private _scenario: Scenario | null = null;
  enableScenarioSharing = true;
  isBatchScenario = false;
  dialogMode = DialogMode;
  private _ownScenarios: string[] = [];
  @Input()
  set ownScenarios(value: string[]) {
    this._ownScenarios = value || [];
    this.setScenarioNameValidator();
  }
  get ownScenarios(): string[] {
    return this._ownScenarios;
  }
  @Input() mode: DialogMode;

  @Input()
  set scenario(newValue: Scenario | null) {
    this._scenario = newValue;
    this.updatePermissionLevelState();
    this.setScenarioNameValidator();
  }
  get scenario(): Scenario | null {
    return this._scenario;
  }

  private _userScenarios: string[] = [];
  @Input()
  set userScenarios(newValue: string[]) {
    this._userScenarios = [...newValue];
    // Include initial base scenario names to ensure validation passes for existing values
    const initialBaseNames = this._scenario?.base
      ? this._scenario.base.map((b) => b.supplyPointGroup.name)
      : [];
    this.baseScenarios = [
      ...new Set([...this._userScenarios, ...initialBaseNames, 'Master'])
    ];
    this.deltaScenarios = this._userScenarios;
    // Add validator to avoid scenarios with the same name
    this.setScenarioNameValidator();
    this.initializeDeltaScenariosList();
  }

  /**
   * Sets the scenario name validator with the latest values.
   * Call this whenever ownScenarios, sharedScenarioNames, permissionLevel, or scenario changes.
   */
  setScenarioNameValidator() {
    if (!this.addEditScenarioForm) return;
    this.addEditScenarioForm.controls.scenarioName.setValidators([
      Validators.required,
      itemNameValidator(
        this.ownScenarios,
        this.sharedScenarioNames,
        this.addEditScenarioForm?.get('permissionLevel')?.value,
        this.scenario?.name,
        this.scenario?.isShared === true
      )
    ]);
    this.addEditScenarioForm.controls.scenarioName.updateValueAndValidity();
  }
  get userScenarios(): string[] {
    return this._userScenarios;
  }

  private _sharedScenarios: Scenario[] = [];
  sharedScenarioNames: string[] = [];
  @Input()
  set sharedScenarios(value: Scenario[]) {
    this._sharedScenarios = value;
    this.sharedScenarioNames = value.map((scenario) => scenario.name);
    this.populateItemTooltips();
    this.setScenarioNameValidator();
  }
  get sharedScenarios(): Scenario[] {
    return this._sharedScenarios;
  }

  @Input()
  deltaHint =
    $localize`:@@createsSnapshot:Creates a snapshot - changes to original won't affect this scenario`;
  @Input()
  baseHint =
    $localize`:@@staysSynced:Stays synced - updates to the base will reflect here`;

  addEditScenarioForm: UntypedFormGroup;
  baseScenarios: string[] = [];
  baseFixedScenarios = ['Master'];
  deltaScenarios: string[];
  itemTooltips: Record<string, string> = {};

  constructor(
    private fb: UntypedFormBuilder,
    private UIStore$: Store<fromUIStore.State>
  ) {}

  ngOnInit(): void {
    const initialValue: Scenario = this.scenario || ({} as Scenario);
    this.addEditScenarioForm = this.createScenarioForm(initialValue);
    this.subscription.add(
      this.UIStore$.select(fromUIStore.enableScenarioSharing).subscribe(
        (value) => {
          this.enableScenarioSharing = value;
        }
      )
    );
    this.subscription.add(
      this.UIStore$.select(fromUIStore.isBatchScenario).subscribe((value) => {
        this.isBatchScenario = value;
        this.updatePermissionLevelState();
      })
    );

    // Watch for changes to scenarioBatchTypeSelection (for Add mode)
    this.subscription.add(
      this.addEditScenarioForm
        .get('scenarioBatchTypeSelection')
        ?.valueChanges.subscribe(() => {
          this.updatePermissionLevelState();
        })
    );

    // Watch for changes to permissionLevel to enable/disable scenarioName
    this.subscription.add(
      this.addEditScenarioForm
        .get('permissionLevel')
        ?.valueChanges.subscribe((value) => {
          this.updateScenarioNameState(value);
          this.setScenarioNameValidator();
        })
    );

    // Initial state
    this.updatePermissionLevelState();
    this.updateScenarioNameState(
      this.addEditScenarioForm.get('permissionLevel')?.value
    );
  }

  updateScenarioNameState(permissionLevel: string) {
    const scenarioNameControl = this.addEditScenarioForm?.get('scenarioName');
    if (!scenarioNameControl) return;
    if (permissionLevel === 'shared' && this.mode === this.dialogMode.Edit) {
      scenarioNameControl.disable({ emitEvent: false });
    } else {
      scenarioNameControl.enable({ emitEvent: false });
    }
  }

  updatePermissionLevelState(): void {
    const permissionLevelControl =
      this.addEditScenarioForm?.get('permissionLevel');
    if (!permissionLevelControl) return;

    if (this.shouldDisablePermissionLevelForBatchScenario()) {
      permissionLevelControl.setValue('private', { emitEvent: false });
      permissionLevelControl.disable({ emitEvent: false });
      return;
    }

    if (this.isPermissionLevelDisabledForNotCreator()) {
      permissionLevelControl.setValue('shared', { emitEvent: false });
      permissionLevelControl.disable({ emitEvent: false });
      return;
    }

    permissionLevelControl.enable({ emitEvent: false });
  }

  isPermissionLevelDisabledForNotCreator(): boolean {
    return (
      this.mode === this.dialogMode.Edit &&
      this.scenario?.isShared === true &&
      this.scenario?.isCreator === false
    );
  }

  private shouldDisablePermissionLevelForBatchScenario(): boolean {
    // Edit mode: disable if scenario is batch
    if (this.mode === this.dialogMode.Edit && this.scenario?.isBatch) {
      return true;
    }
    // Add mode: disable if batch scenario type is selected
    if (this.mode === this.dialogMode.Add && this.isBatchScenario) {
      return (
        this.addEditScenarioForm?.get('scenarioBatchTypeSelection')?.value ===
        'batch'
      );
    }
    return false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  createScenarioForm(initialScenarioValue: Scenario) {
    var form = this.fb.group({
      id: initialScenarioValue.id,
      scenarioName: [initialScenarioValue.name],
      permissionLevel: [
        initialScenarioValue.isShared ? 'shared' : 'private',
        Validators.required
      ],
      baseScenarios: [
        initialScenarioValue.base
          ? [
              ...new Set([
                ...this.baseFixedScenarios,
                ...initialScenarioValue.base.map((b) => b.supplyPointGroup.name)
              ])
            ]
          : this.baseFixedScenarios
      ],
      deltaScenarios: [],
      scenarioBatchTypeSelection: [
        initialScenarioValue.isBatch ? 'batch' : 'standard',
        Validators.required
      ]
    });

    return form;
  }

  emptyScenarioName() {
    if (this.addEditScenarioForm.controls.scenarioName.value) {
      return (
        this.addEditScenarioForm.controls.scenarioName.value?.trim().length == 0
      );
    }

    return this.addEditScenarioForm.controls.scenarioName.errors?.required;
  }

  uniqueScenarioName() {
    return !this.addEditScenarioForm.controls.scenarioName.errors
      ?.valueAlreadyExist;
  }

  invalidForm(): boolean {
    return (
      !(this.addEditScenarioForm && this.addEditScenarioForm.controls) ||
      !this.addEditScenarioForm.dirty ||
      (this.addEditScenarioForm.controls?.scenarioName.enabled &&
        !this.addEditScenarioForm.controls?.scenarioName.valid) ||
      !this.addEditScenarioForm.controls?.baseScenarios.valid ||
      !this.addEditScenarioForm.controls?.deltaScenarios.valid ||
      (this.addEditScenarioForm?.controls?.permissionLevel?.enabled &&
        !this.addEditScenarioForm.controls?.permissionLevel.valid) ||
      this.emptyScenarioName() ||
      !this.uniqueScenarioName() ||
      this.hasSharedNameConflict()
    );
  }

  /**
   * Checks if the current scenario name conflicts when changing permission to shared.
   * This is needed because the scenarioName control is disabled in edit mode when
   * sharing, which causes Angular to skip validators.
   */
  hasSharedNameConflict(): boolean {
    const scenarioNameControl = this.addEditScenarioForm?.get('scenarioName');
    if (!scenarioNameControl || scenarioNameControl.enabled) {
      return false; // Let the normal validator handle it when enabled
    }

    const validator = itemNameValidator(
      this.ownScenarios,
      this.sharedScenarioNames,
      this.addEditScenarioForm?.get('permissionLevel')?.value,
      this.scenario?.name,
      this.scenario?.isShared === true
    );

    // Manually run the validator with the current/original name
    const mockControl = { value: this.scenario?.name } as AbstractControl;
    const errors = validator(mockControl);
    return errors?.['valueAlreadyExist'] === true;
  }

  onCancel() {
    // Implement cancel logic or emit event
  }

  populateItemTooltips(): void {
    this.itemTooltips = {};
    this.sharedScenarios.forEach((scenario) => {
      this.itemTooltips[scenario.name] =
        `Created by ${formatUserInitials(scenario.creatorName)}`;
    });
  }

  // Initialize the Delta scenarios list removing those scenarios already chosen in Base scenario list
  initializeDeltaScenariosList() {
    const selectedBaseScenarios = this._scenario?.base
      ? this._scenario.base.map((b) => b.supplyPointGroup.name)
      : [];

    this.onBaseScenariosChanged(selectedBaseScenarios);
  }

  onDeltaScenariosChanged(values: any) {
    // Selected Delta Scenarios cannot be present in baseScenarios array
    // Include initial base scenario names to ensure validation passes for existing values
    const initialBaseNames = this._scenario?.base
      ? this._scenario.base.map((b) => b.supplyPointGroup.name)
      : [];
    this.baseScenarios = [
      ...new Set([
        ...(this.userScenarios || []).filter((item) => !values.includes(item)),
        ...initialBaseNames,
        'Master'
      ])
    ];
  }

  onBaseScenariosChanged(values: any) {
    // Selected Base Scenarios cannot be present in deltaScenarios array
    this.deltaScenarios = this.userScenarios.filter(
      (item) => !values.includes(item)
    );
  }
}
